/** The data grid of the table pages - ADR-0212. A spreadsheet's look with the product's rules.
 *
 * Every cell is bordered, the header row is tinted and sticky, a tick-box column sits at the left,
 * alternate rows are shaded, and numbers are right-aligned in tabular figures. A row-number column is
 * available (`rowNumbers`) and **off by default**: a list page's rows are not a spreadsheet's. An
 * optional totals row sits under the body.
 *
 * **Wide tables scroll, and say so.** The tick-box column and the first data column are pinned to the
 * left (`position: sticky`), so a row keeps its name while its figures scroll past; a shadow on the
 * right edge of the frame shows there is more to the right, until the last column is reached. The
 * last column is never clipped without a signal: the scroll area is a focusable, labelled region
 * whenever it overflows, and the shadow is drawn from the same measurement.
 *
 * **It never computes a figure.** The rows are the page the server sent, `totals` are the server's
 * strings (a sum made here would be a figure with no receipt, CLAUDE.md rule 3), the row numbers
 * count the rows shown from `rowNumberStart`, and a sort is *reported* to the caller (`onSortChange`),
 * who orders the rows. The one sum this file makes is a **group subtotal**, and only for a column the
 * page marked `summable`: exact decimal arithmetic (`decimal.ts`), over the rows the grid holds, never
 * in the footer, where the server's total stays.
 *
 * **Power features are opt-in.** Pass `power` (from `useGridPower`) and the grid gains, in this file:
 * range selection (shift+click) with an indeterminate header box and a "select all N matching" banner;
 * a menu on every header (sort, filter this column, group by, hide, pin left; shift+click a header adds
 * a sort level); collapsible groups with a count and the subtotals of the summable columns; the
 * density rows (28/36/48 px); a quick-peek panel (`renderPeek`); cell-by-cell keyboard movement; and
 * the shortcut sheet on `?`. Without `power` it behaves as it always did.
 *
 * Keyboard (the grid is a native `table`, so a screen reader meets rows and cells):
 * - a header is a sort button when its column is `sortable`, with `aria-sort` on the `th`;
 * - with `onRowOpen` (or `power`), rows are a roving tab stop: ArrowUp, ArrowDown, Home, End and
 *   PageUp/PageDown move between rows, ArrowRight enters the cells and the arrows move between them,
 *   Enter opens the row (or activates the control in the focused cell), and Space opens the row - or,
 *   with a peek, the quick-peek; a click that lands on a link, button or box inside the row keeps its
 *   own behaviour and does not also open it;
 * - the tick boxes are native checkboxes, reached with Tab and toggled with Space; the header box
 *   selects the rows shown and is `mixed` when only some are.
 *
 * States are designed, not defaulted. **Loading** keeps the header and shows skeleton rows only after
 * 150 ms; **empty** says why and offers the way out; **error** is one row - "Couldn't load" and Retry -
 * and never zeros: a failed read leaves no totals and no rows.
 *
 * `columnMenu` is the hook for a caller's own header addition (an info control): the table renders
 * whatever the caller returns beside the header's label and decides nothing about it.
 */

import { ArrowClockwise, CaretDown, CaretRight, CaretUp, CaretUpDown, Eye } from "@phosphor-icons/react";
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, KeyboardEvent, MouseEvent, ReactNode, RefObject } from "react";

import { readerLocale } from "../../lib/format";
import { EmptyState } from "./EmptyState";
import styles from "./GridTable.module.css";
import { Skeleton } from "./Skeleton";
import type { SortState } from "./sortRows";
import { ColumnMenu } from "./tablePower/ColumnMenu";
import { isDecimal, sumDecimals } from "./tablePower/decimal";
import type { GridColumn, GridTotals } from "./tablePower/gridTypes";
import { DENSITY_HEIGHT } from "./tablePower/gridTypes";
import { PeekPanel } from "./tablePower/PeekPanel";
import { rangeBetween } from "./tablePower/selection";
import { ShortcutSheet } from "./tablePower/ShortcutSheet";
import power_styles from "./tablePower/tablePower.module.css";
import { useDelayedFlag } from "./tablePower/useDelayedFlag";
import type { GridPower } from "./tablePower/useGridPower";
import { useRowWindow } from "./tablePower/useRowWindow";

export type { GridColumn, GridSummable, GridTotals } from "./tablePower/gridTypes";

export interface GridTableProps<T> {
  /** The table's accessible name, and the scroll region's when it overflows. */
  caption: string;
  columns: readonly GridColumn<T>[];
  rows: readonly T[];
  rowKey: (row: T) => string;
  /** A human name for a row: the tick box's label ("Select {name}"). */
  rowLabel?: (row: T) => string;
  rowNumbers?: boolean;
  /** The number of the first row shown. 1 for the first page; a later page passes its offset + 1. */
  rowNumberStart?: number;
  selectable?: boolean;
  selectedKeys?: ReadonlySet<string>;
  onSelectionChange?: (next: ReadonlySet<string>) => void;
  onRowOpen?: (row: T) => void;
  zebra?: boolean;
  totals?: GridTotals;
  columnMenu?: (column: GridColumn<T>) => ReactNode;
  sort?: SortState | null;
  onSortChange?: (sort: SortState | null) => void;
  /** Scroll the body inside the grid so the sticky header has something to stick to. */
  maxHeight?: string;
  loading?: boolean;
  loadingRows?: number;
  /** How long a read must take before skeleton rows are drawn. Default 150 ms. */
  loadingDelayMs?: number;
  /** Shown in place of the body when there are no rows. A failed read is never the empty state. */
  empty?: ReactNode;
  /** The read failed: one row, "Couldn't load" and Retry, in place of the rows and the totals. */
  error?: { onRetry: () => void; label?: string };
  testId?: string;
  /** Extra `data-*` attributes for a row (a test id, a state a test or the gate can read). */
  rowAttributes?: (row: T) => Record<`data-${string}`, string | undefined>;
  /** The power features' state (`useGridPower`). Absent: the plain grid. */
  power?: GridPower;
  /** The quick-peek panel's body for a row: the page's receipt summary. Present: Space and the peek
   * icon open it. */
  renderPeek?: (row: T) => ReactNode;
  /** How many rows the filter matches in all, when more than are loaded (a paged list). It drives the
   * "Select all N matching this filter" banner. */
  totalMatching?: number;
  /** Called when "Select all N matching" is chosen, so a paged list can load the rest. */
  onSelectAllMatching?: () => void;
}

/** Whether the scroll area overflows at all, and whether there is still more to its right. */
function useScrollState(ref: RefObject<HTMLElement | null>): { overflows: boolean; more: boolean } {
  const [state, setState] = useState({ overflows: false, more: false });
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    const check = () => {
      const next = {
        overflows: node.scrollWidth > node.clientWidth + 1 || node.scrollHeight > node.clientHeight + 1,
        more: node.scrollLeft + node.clientWidth < node.scrollWidth - 1,
      };
      setState((prev) => (prev.overflows === next.overflows && prev.more === next.more ? prev : next));
    };
    check();
    node.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    // The frame can stay the same size while what is inside it grows (rows arrive after the first
    // measurement, a density change, a column shown): watching only the frame left a table that
    // scrolled and was not a focusable region (axe `scrollable-region-focusable` on the month tabs).
    const observer = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(check);
    observer?.observe(node);
    for (const child of Array.from(node.children)) observer?.observe(child);
    return () => {
      node.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
      observer?.disconnect();
    };
  }, [ref]);
  return state;
}

/** The width of the tick-box column (wider with a peek icon) and of the row-number column, which the
 * pinned columns offset by. */
const TICK_WIDTH = 36;
const TICK_WIDTH_PEEK = 54;
const NUMBER_WIDTH = 44;
const PAGE_ROWS = 10;

const INTERACTIVE = "a,button,input,select,textarea,label,[role='button'],[role='menuitem']";
const EMPTY_KEYS: ReadonlySet<string> = new Set();

/** A px length (`"120px"`, `"96"`) as a number, or null for anything else (`20%`, `auto`). */
function px(length: string | undefined): number | null {
  if (length === undefined) return null;
  const match = /^(\d+(?:\.\d+)?)(?:px)?$/.exec(length.trim());
  return match ? Number(match[1]) : null;
}

const FLEX_MIN = 160;

/** The narrowest a fixed-track table may be drawn: each stated width plus the flexible columns' minimums,
 * and the tick-box and row-number columns. Below it the card scrolls and fades; above it the flexible
 * column takes the room. Undefined when a width is not in px, so the table is then only as wide as its card. */
function fitMinWidth<T>(columns: readonly GridColumn<T>[], lead: number, number: number): number | undefined {
  let sum = lead + number;
  for (const column of columns) {
    const stated = px(column.width);
    if (column.width !== undefined && stated === null) return undefined;
    sum += stated ?? px(column.minWidth) ?? FLEX_MIN;
  }
  return sum;
}

/** A cell as plain text, for Export and for naming a group. Never "[object Object]". */
function textOf<T>(column: GridColumn<T>, row: T, index: number): string {
  if (column.value) {
    const value = column.value(row);
    return value === null || value === undefined ? "" : String(value);
  }
  const cell = column.cell(row, index);
  return typeof cell === "string" || typeof cell === "number" ? String(cell) : "";
}

/** A cell is one line and is cut with an ellipsis, so its whole text is its title: the column's plain value
 * when it has one, else the cell itself when that is text. Short text is not repeated as a tooltip. */
const TITLE_MIN = 18;
function cutTitle<T>(column: GridColumn<T>, row: T, content: ReactNode): string | undefined {
  if (column.wrap) return undefined;
  const raw = column.value ? column.value(row) : typeof content === "string" || typeof content === "number" ? content : null;
  if (raw === null || raw === undefined) return undefined;
  const text = String(raw);
  return text.length >= TITLE_MIN ? text : undefined;
}

/** A rendered row of this grid by its position among the rows drawn. Found in the document by the
 * grid's id, so the keyboard code reads no ref during render. */
function rowAt(gridId: string, index: number): HTMLElement | null {
  return document.querySelector<HTMLElement>(`[data-grid-id="${gridId}"] tr[data-row-index="${String(index)}"]`);
}

/** Group labels in the reader's own collation (the one locale context, `lib/format.ts`). */
const naturalOrder = (a: string, b: string) =>
  a.localeCompare(b, readerLocale(), { numeric: true, sensitivity: "base" });

type Item<T> =
  | { kind: "group"; label: string; rows: readonly T[] }
  | { kind: "row"; row: T; at: number };

export function GridTable<T>({
  caption,
  columns,
  rows,
  rowKey,
  rowLabel,
  rowNumbers = false,
  rowNumberStart = 1,
  selectable = false,
  selectedKeys,
  onSelectionChange,
  onRowOpen,
  zebra = true,
  totals,
  columnMenu,
  sort,
  onSortChange,
  maxHeight,
  loading = false,
  loadingRows = 6,
  loadingDelayMs = 150,
  empty,
  error,
  testId = "grid-table",
  rowAttributes,
  power,
  renderPeek,
  totalMatching,
  onSelectAllMatching,
}: GridTableProps<T>) {
  const scroller = useRef<HTMLDivElement>(null);
  const { overflows, more } = useScrollState(scroller);
  const gridId = useId();
  const headRefs = useRef<Record<string, HTMLTableCellElement | null>>({});
  const [anchor, setAnchor] = useState<string | null>(null);
  const [active, setActive] = useState(0);
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(EMPTY_KEYS);
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const allBox = useRef<HTMLInputElement>(null);
  const showSkeleton = useDelayedFlag(loading, loadingDelayMs);

  // ---- which columns, in which order
  const first = columns[0];
  const hiddenIds = power?.hidden;
  const pinnedCols = useMemo(() => {
    const ids = power?.pinned ?? [];
    return ids
      .map((id) => columns.find((c) => c.id === id))
      .filter((c): c is GridColumn<T> => c !== undefined && c !== first && !hiddenIds?.has(c.id));
  }, [power?.pinned, columns, first, hiddenIds]);
  const visible = useMemo(() => {
    if (!first) return [] as GridColumn<T>[];
    const rest = columns.filter((c) => c !== first && !pinnedCols.includes(c) && !hiddenIds?.has(c.id));
    return [first, ...pinnedCols, ...rest];
  }, [columns, first, pinnedCols, hiddenIds]);
  const pinnedCount = visible.length === 0 ? 0 : 1 + pinnedCols.length;

  // ---- rows, groups and the keys the keyboard walks
  const keys = rows.map(rowKey);
  const groupColumn = power?.groupBy ? columns.find((c) => c.id === power.groupBy) : undefined;
  const items = useMemo<Item<T>[]>(() => {
    const out: Item<T>[] = [];
    if (!groupColumn) {
      rows.forEach((row, at) => out.push({ kind: "row", row, at }));
      return out;
    }
    const groups = new Map<string, T[]>();
    rows.forEach((row, at) => {
      const label = textOf(groupColumn, row, at) || "(none)";
      const list = groups.get(label);
      if (list) list.push(row);
      else groups.set(label, [row]);
    });
    let at = 0;
    for (const label of [...groups.keys()].sort(naturalOrder)) {
      const list = groups.get(label) as T[];
      out.push({ kind: "group", label, rows: list });
      if (!collapsed.has(label)) for (const row of list) out.push({ kind: "row", row, at: at++ });
    }
    return out;
  }, [rows, groupColumn, collapsed]);
  const dataRows = useMemo(
    () => items.flatMap((item) => (item.kind === "row" ? [item.row] : [])),
    [items],
  );
  const dataKeys = dataRows.map(rowKey);
  // Which item (a row or a group heading) each data row is, for the keyboard to reveal a row that the
  // window has left out.
  const itemOfData = useMemo(
    () => items.flatMap((item, at) => (item.kind === "row" ? [at] : [])),
    [items],
  );
  const tbodyRef = useRef<HTMLTableSectionElement>(null);
  const rowWindow = useRowWindow(items.length, scroller, tbodyRef, DENSITY_HEIGHT[power?.density ?? "comfortable"]);

  // ---- selection: the power state, or the caller's
  const selected: ReadonlySet<string> = power
    ? power.allMatching
      ? new Set(keys)
      : power.selected
    : (selectedKeys ?? EMPTY_KEYS);
  const setSelected = (next: ReadonlySet<string>) => {
    if (power) power.setSelected(next);
    else onSelectionChange?.(next);
  };
  const chosen = keys.filter((key) => selected.has(key)).length;
  const all = rows.length > 0 && chosen === rows.length;
  const some = chosen > 0 && !all;
  const selectionActive = (power?.selectedCount ?? chosen) > 0;

  useEffect(() => {
    if (allBox.current) allBox.current.indeterminate = some;
  }, [some]);

  // Rows that left the list (a filter, a page) are no longer selected: a bulk action on a row nobody
  // can see is the dangerous kind.
  const keySignature = keys.join("\u0000");
  useEffect(() => {
    if (!power || loading || power.allMatching) return;
    const present = new Set(keySignature === "" ? [] : keySignature.split("\u0000"));
    const stale = [...power.selected].some((key) => !present.has(key));
    if (stale) power.setSelected(new Set([...power.selected].filter((key) => present.has(key))));
    // `power.selected` is the thing pruned; the signature says when the rows changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keySignature, loading]);

  const lead = (rowNumbers ? 1 : 0) + (selectable ? 1 : 0);
  const tickWidth = renderPeek && power ? TICK_WIDTH_PEEK : TICK_WIDTH;
  const pinnedLeft = (selectable ? tickWidth : 0) + (rowNumbers ? NUMBER_WIDTH : 0);
  const numberLeft = selectable ? tickWidth : 0;
  const span = lead + visible.length;
  const cellNav = onRowOpen !== undefined || power !== undefined;

  // The pinned columns after the first sit at the sum of the widths before them. The offsets are
  // measured from the header cells and written to the table as CSS variables (`--pin-1`, ...), which
  // the cells name in their `left`; no state, so a measurement cannot loop back into a render.
  useLayoutEffect(() => {
    const table = tableRef.current;
    if (!table) return;
    let left = pinnedLeft;
    visible.slice(0, pinnedCount).forEach((column, at) => {
      table.style.setProperty(`--pin-${String(at)}`, `${String(left)}px`);
      left += headRefs.current[column.id]?.offsetWidth ?? 0;
    });
    // The pinned columns sit over whatever scrolls beneath them, so a control the browser scrolls
    // into view (Tab to a header button, a row's link) must stop short of them rather than land
    // under them: the scroll area keeps this much clear at its left edge.
    scroller.current?.style.setProperty("scroll-padding-left", `${String(left)}px`);
    // And the header row is held at the top the same way: a row's control scrolled into view clears it.
    scroller.current?.style.setProperty("scroll-padding-top", `${String(table.tHead?.offsetHeight ?? 0)}px`);
  });

  // ---- Export reads exactly what is on screen
  useEffect(() => {
    if (!power) return;
    // **A row-menu column is not data** (G-P7-14): a column whose header is visually hidden and that
    // states no plain `value` holds controls (a menu, a button), so the file leaves it out and the
    // summary does not count it. A hidden-header column that does state a value is data and stays.
    const exported = visible.filter((c) => !(c.headerHidden && c.value === undefined));
    power.bind({
      columns: columns.map((c, at) => ({
        id: c.id,
        header: c.header,
        hideable: at !== 0 && c.hideable !== false,
        groupable: c.groupable ?? c.value !== undefined,
      })),
      rowCount: rows.length,
      columnCount: exported.length,
      unlisted: totalMatching !== undefined ? Math.max(0, totalMatching - rows.length) : 0,
      matrix: (selectedOnly) => {
        const picked = selectedOnly ? rows.filter((row) => selected.has(rowKey(row))) : rows;
        return {
          header: exported.map((c) => c.header),
          rows: picked.map((row, at) => exported.map((c) => textOf(c, row, at))),
        };
      },
    });
  });
  const unbind = power?.bind;
  useEffect(() => () => unbind?.(null), [unbind]);

  // ---- keyboard
  const peekEnabled = renderPeek !== undefined && power !== undefined;
  const peekKey = power?.peekKey ?? null;
  const focusRow = (index: number) => focusCell(index, -1);
  const focusCell = (index: number, column: number) => {
    const clamped = Math.max(0, Math.min(dataRows.length - 1, index));
    setActive(clamped);
    const focusIt = () => {
      const row = rowAt(gridId, clamped);
      const target = column >= 0 ? row?.querySelector<HTMLElement>(`[data-cell="${String(column)}"]`) : null;
      (target ?? row)?.focus();
    };
    if (rowWindow.active && !rowAt(gridId, clamped)) {
      // Not drawn yet: draw it (the state change is flushed when this handler returns), then focus it
      // on the next frame, when it exists.
      rowWindow.reveal(itemOfData[clamped] ?? 0);
      window.setTimeout(focusIt, 0);
    } else {
      focusIt();
    }
    if (peekEnabled && peekKey !== null) power.setPeekKey(dataKeys[clamped] ?? null);
  };
  const closePeek = () => {
    const at = peekKey === null ? -1 : dataKeys.indexOf(peekKey);
    power?.setPeekKey(null);
    if (at >= 0) rowAt(gridId, at)?.focus();
  };

  const toggle = (key: string, shift: boolean) => {
    const next = new Set(selected);
    if (shift && anchor !== null) {
      for (const inRange of rangeBetween(dataKeys, anchor, key)) next.add(inRange);
    } else if (next.has(key)) next.delete(key);
    else next.add(key);
    setAnchor(key);
    setSelected(next);
  };

  const onRowKey = (event: KeyboardEvent<HTMLTableRowElement>, row: T, index: number) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const onRow = target === event.currentTarget;
    const onCell = target.hasAttribute("data-cell");
    if (!onRow && !onCell) return;
    const column = onCell ? Number(target.dataset.cell) : -1;
    const lastColumn = visible.length - 1;
    const go = (to: number, col = column) => (col >= 0 ? focusCell(to, col) : focusRow(to));
    const area = event.currentTarget.closest<HTMLElement>("[data-grid-scroll]");
    const page = Math.max(1, Math.floor((area?.clientHeight ?? 0) / Math.max(1, event.currentTarget.offsetHeight)) - 1);
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        go(index + 1);
        break;
      case "ArrowUp":
        event.preventDefault();
        go(index - 1);
        break;
      case "PageDown":
        event.preventDefault();
        go(index + (page > 1 ? page : PAGE_ROWS));
        break;
      case "PageUp":
        event.preventDefault();
        go(index - (page > 1 ? page : PAGE_ROWS));
        break;
      case "ArrowRight":
        if (!cellNav) break;
        event.preventDefault();
        focusCell(index, Math.min(lastColumn, column + 1));
        break;
      case "ArrowLeft":
        if (!cellNav || column < 0) break;
        event.preventDefault();
        if (column === 0) focusRow(index);
        else focusCell(index, column - 1);
        break;
      case "Home":
        event.preventDefault();
        if (onCell && !event.ctrlKey) focusCell(index, 0);
        else go(0, onCell ? 0 : -1);
        break;
      case "End":
        event.preventDefault();
        if (onCell && !event.ctrlKey) focusCell(index, lastColumn);
        else go(dataRows.length - 1, onCell ? lastColumn : -1);
        break;
      case "Enter": {
        event.preventDefault();
        const inner = onCell ? target.querySelector<HTMLElement>("a,button") : null;
        if (inner) inner.click();
        else onRowOpen?.(row);
        break;
      }
      case " ":
        event.preventDefault();
        if (peekEnabled) {
          const key = dataKeys[index] ?? null;
          power.setPeekKey(peekKey === key ? null : key);
        } else {
          onRowOpen?.(row);
        }
        break;
      default:
    }
  };

  const onRowClick = (event: MouseEvent<HTMLTableRowElement>, row: T) => {
    if (!onRowOpen) return;
    const target = event.target;
    if (target instanceof Element && target.closest(INTERACTIVE)) return;
    // A click that ends a text selection is not a request to open the row.
    if (window.getSelection()?.toString()) return;
    onRowOpen(row);
  };

  // Escape clears the selection - unless a peek is open, which Escape closes first.
  const onFrameKey = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "Escape" && power && power.selectedCount > 0 && peekKey === null && !event.defaultPrevented) {
      event.preventDefault();
      power.clearSelection();
    }
  };

  // `?` anywhere outside a text field opens the shortcut sheet.
  useEffect(() => {
    if (!power) return;
    const open = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "?" || event.ctrlKey || event.metaKey || event.altKey) return;
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))
      ) {
        return;
      }
      event.preventDefault();
      power.setShortcutsOpen(true);
    };
    document.addEventListener("keydown", open);
    return () => document.removeEventListener("keydown", open);
  }, [power]);

  // ---- sorting
  const sorts: readonly SortState[] = power ? power.sorts : sort ? [sort] : [];
  const sortFor = (column: GridColumn<T>): "ascending" | "descending" | "none" | undefined => {
    if (!column.sortable) return undefined;
    const level = sorts.find((s) => s.columnId === column.id);
    if (!level) return "none";
    return level.direction === "asc" ? "ascending" : "descending";
  };
  const cycle = (column: GridColumn<T>, additive: boolean) => {
    if (power) {
      power.cycleSort(column.id, additive);
      return;
    }
    if (!onSortChange) return;
    if (sort?.columnId !== column.id) onSortChange({ columnId: column.id, direction: "asc" });
    else if (sort.direction === "asc") onSortChange({ columnId: column.id, direction: "desc" });
    else onSortChange(null);
  };

  // ---- fixed tracks: a table that budgets its columns fits its card (U6, 2026-11-03)
  const fit = visible.some((c) => c.width !== undefined);
  const fitMin = fit ? fitMinWidth(visible, selectable ? tickWidth : 0, rowNumbers ? NUMBER_WIDTH : 0) : undefined;
  const alignOf = (column: GridColumn<T>): "left" | "right" | "center" =>
    column.align ?? (column.numeric ? "right" : "left");
  const pinStyle = (at: number): CSSProperties | undefined =>
    at === 0 || at >= pinnedCount ? undefined : { left: `var(--pin-${String(at)})` };
  const cellClass = (at: number) => `${styles.cell} ${at < pinnedCount ? styles.pinned : ""}`;

  const sortLevel = (column: GridColumn<T>): number | null => {
    if (sorts.length < 2) return null;
    const at = sorts.findIndex((s) => s.columnId === column.id);
    return at < 0 ? null : at + 1;
  };

  const hasMenu = power !== undefined;
  const filterFor = (column: GridColumn<T>) => {
    const id = column.filterId ?? column.id;
    return power?.filterDefs.some((d) => d.id === id) ? id : null;
  };

  // The banner offering every row the filter matches, beyond the rows loaded.
  const offerAll =
    power !== undefined &&
    !power.allMatching &&
    totalMatching !== undefined &&
    totalMatching > rows.length &&
    rows.length > 0 &&
    all;

  const body = (() => {
    if (error) {
      return (
        <tr>
          <td colSpan={span} className={styles.empty}>
            <div className={styles.errorRow} role="alert" data-testid={`${testId}-error`}>
              <span>{error.label ?? "Couldn't load"}</span>
              <button type="button" className={power_styles.tool} onClick={error.onRetry} data-testid={`${testId}-retry`}>
                <ArrowClockwise size={14} aria-hidden="true" />
                Retry
              </button>
            </div>
          </td>
        </tr>
      );
    }
    if (loading) {
      // The header stays and the body waits out the delay, but the wait is announced at once.
      if (!showSkeleton) {
        return (
          <tr>
            <td colSpan={span} className="visuallyHidden">
              <span role="status">Loading…</span>
            </td>
          </tr>
        );
      }
      return Array.from({ length: loadingRows }, (_, at) => (
        <tr key={at} data-skeleton-row>
          {selectable ? <td className={styles.tick} /> : null}
          {rowNumbers ? <td className={styles.number} /> : null}
          {visible.map((column, col) => (
            <td key={column.id} className={cellClass(col)} data-align={alignOf(column)}>
              {at === 0 && col === 0 ? (
                <span className="visuallyHidden" role="status">
                  Loading…
                </span>
              ) : null}
              <Skeleton width={`${String(90 - ((at * 11 + col * 17) % 45))}%`} />
            </td>
          ))}
        </tr>
      ));
    }
    if (rows.length === 0) {
      const narrowed =
        power !== undefined && (power.filters.search.trim() !== "" || Object.values(power.filters.values).some((v) => v.length));
      return (
        <tr>
          <td colSpan={span} className={styles.empty}>
            {empty ?? (
              <EmptyState
                compact
                title={narrowed ? "No rows match these filters" : "Nothing here yet"}
                action={
                  narrowed ? (
                    <button
                      type="button"
                      className={power_styles.tool}
                      onClick={() => power.setFilters({ search: "", values: {} })}
                    >
                      Clear filters
                    </button>
                  ) : null
                }
              />
            )}
          </td>
        </tr>
      );
    }
    const drawn = rowWindow.active ? items.slice(rowWindow.start, rowWindow.end) : items;
    const rendered = drawn.map((item) => {
      if (item.kind === "group") {
        const isCollapsed = collapsed.has(item.label);
        const groupKeys = item.rows.map(rowKey);
        const picked = groupKeys.filter((key) => selected.has(key)).length;
        return (
          <tr key={`group:${item.label}`} className={styles.groupRow} data-group={item.label} data-collapsed={isCollapsed || undefined}>
            {selectable ? (
              <td className={styles.tick}>
                <input
                  type="checkbox"
                  checked={picked === groupKeys.length}
                  ref={(node) => {
                    if (node) node.indeterminate = picked > 0 && picked < groupKeys.length;
                  }}
                  aria-label={`Select group ${item.label}`}
                  onChange={() => {
                    const next = new Set(selected);
                    for (const key of groupKeys) {
                      if (picked === groupKeys.length) next.delete(key);
                      else next.add(key);
                    }
                    setSelected(next);
                  }}
                />
              </td>
            ) : null}
            {rowNumbers ? <td className={styles.number} /> : null}
            {visible.map((column, col) => {
              if (col === 0) {
                return (
                  <th
                    key={column.id}
                    scope="rowgroup"
                    className={cellClass(col)}
                    data-align="left"
                  >
                    <button
                      type="button"
                      className={styles.groupToggle}
                      aria-expanded={!isCollapsed}
                      onClick={() => {
                        const next = new Set(collapsed);
                        if (isCollapsed) next.delete(item.label);
                        else next.add(item.label);
                        setCollapsed(next);
                      }}
                    >
                      {isCollapsed ? <CaretRight size={12} weight="bold" aria-hidden="true" /> : <CaretDown size={12} weight="bold" aria-hidden="true" />}
                      <span>{item.label}</span>
                      <span className={styles.groupCount} data-testid="group-count">
                        {item.rows.length}
                      </span>
                    </button>
                  </th>
                );
              }
              const summable = column.summable;
              let subtotal: ReactNode = null;
              let partial = 0;
              if (summable) {
                const values = item.rows.map((row) => summable.value(row));
                const numbers = values.filter((v): v is string => typeof v === "string" && isDecimal(v));
                partial = values.length - numbers.length;
                const sum = sumDecimals(numbers);
                subtotal = sum === null ? null : summable.format(sum);
              }
              return (
                <td
                  key={column.id}
                  className={cellClass(col)}
                  style={pinStyle(col)}
                  data-align={alignOf(column)}
                  data-numeric={alignOf(column) === "right" ? "true" : undefined}
                  data-subtotal={summable ? column.id : undefined}
                  data-partial={partial > 0 || undefined}
                  title={partial > 0 ? `${String(partial)} without a figure, not in this sum` : undefined}
                >
                  {subtotal}
                </td>
              );
            })}
          </tr>
        );
      }
      const { row, at } = item;
      const key = rowKey(row);
      const name = rowLabel?.(row) ?? `row ${String(rowNumberStart + at)}`;
      const on = selected.has(key);
      const peeked = peekKey === key;
      return (
        <tr
          key={key}
          data-row-index={at}
          className={onRowOpen ? styles.openable : undefined}
          {...rowAttributes?.(row)}
          data-selected={on || undefined}
          data-peeked={peeked || undefined}
          tabIndex={cellNav ? (at === active ? 0 : -1) : undefined}
          onFocus={cellNav ? () => setActive(at) : undefined}
          onKeyDown={cellNav ? (event) => onRowKey(event, row, at) : undefined}
          onClick={onRowOpen ? (event) => onRowClick(event, row) : undefined}
        >
          {selectable ? (
            <td className={styles.tick}>
              <input
                type="checkbox"
                checked={on}
                aria-label={`Select ${name}`}
                onChange={(event) => toggle(key, (event.nativeEvent as MouseEvent["nativeEvent"]).shiftKey === true)}
              />
              {peekEnabled ? (
                <button
                  type="button"
                  className={styles.peekIcon}
                  disabled={selectionActive}
                  aria-label={`Preview ${name}`}
                  data-testid="peek-icon"
                  onClick={() => power.setPeekKey(peeked ? null : key)}
                >
                  <Eye size={14} aria-hidden="true" />
                </button>
              ) : null}
            </td>
          ) : null}
          {rowNumbers ? <td className={styles.number}>{rowNumberStart + at}</td> : null}
          {visible.map((column, col) => {
            const Cell = column.rowHeader ? "th" : "td";
            const content = column.cell(row, at);
            return (
              <Cell
                key={column.id}
                scope={column.rowHeader ? "row" : undefined}
                className={cellClass(col)}
                style={pinStyle(col)}
                title={cutTitle(column, row, content)}
                data-first={col === 0 || undefined}
                data-wrap={column.wrap || undefined}
                data-align={alignOf(column)}
                data-numeric={alignOf(column) === "right" ? "true" : undefined}
                data-cell={cellNav ? col : undefined}
                tabIndex={cellNav ? -1 : undefined}
              >
                {content}
              </Cell>
            );
          })}
        </tr>
      );
    });
    if (!rowWindow.active) return rendered;
    // The rows left out stand in as one empty row above and one below, so the scroll bar is the whole
    // table's. Both are always present, so a row's zebra shade does not change as the window moves.
    return [
      <tr key="spacer:top" data-spacer="top" aria-hidden="true" style={{ height: rowWindow.top }}>
        <td colSpan={span} />
      </tr>,
      ...rendered,
      <tr key="spacer:bottom" data-spacer="bottom" aria-hidden="true" style={{ height: rowWindow.bottom }}>
        <td colSpan={span} />
      </tr>,
    ];
  })();

  const peekRow = peekEnabled && peekKey !== null ? rows.find((row) => rowKey(row) === peekKey) : undefined;

  return (
    <div className={styles.frame} data-more={more || undefined} onKeyDown={power ? onFrameKey : undefined}>
      {offerAll && power ? (
        <div className={power_styles.selectAll} data-testid="select-all-banner">
          <span>{`${String(rows.length)} selected`}</span>
          <button
            type="button"
            className={power_styles.linkButton}
            data-testid="select-all-matching"
            onClick={() => {
              power.selectAllMatching(totalMatching);
              onSelectAllMatching?.();
            }}
          >
            {`Select all ${String(totalMatching)} matching this filter`}
          </button>
        </div>
      ) : null}
      <div
        ref={scroller}
        className={styles.scroll}
        style={maxHeight ? { maxHeight } : undefined}
        role={overflows ? "region" : undefined}
        aria-label={overflows ? caption : undefined}
        tabIndex={overflows ? 0 : undefined}
        data-testid={`${testId}-scroll`}
        data-grid-scroll
      >
        <table
          ref={tableRef}
          className={`${styles.grid} ${zebra ? styles.zebra : ""}`}
          data-testid={testId}
          data-grid-table=""
          data-grid-id={gridId}
          data-density={power?.density}
          data-fit={fit || undefined}
          aria-busy={loading || undefined}
          style={
            {
              "--number-left": `${String(numberLeft)}px`,
              "--pinned-left": `${String(pinnedLeft)}px`,
              "--tick-w": `${String(tickWidth)}px`,
              ...(fitMin !== undefined ? { minWidth: `${String(fitMin)}px` } : {}),
            } as CSSProperties
          }
        >
          <caption className="visuallyHidden">{caption}</caption>
          {fit ? (
            <colgroup>
              {selectable ? <col style={{ width: `${String(tickWidth)}px` }} /> : null}
              {rowNumbers ? <col style={{ width: `${String(NUMBER_WIDTH)}px` }} /> : null}
              {visible.map((column) => (
                <col key={column.id} style={column.width ? { width: column.width } : undefined} />
              ))}
            </colgroup>
          ) : null}
          <thead>
            <tr>
              {selectable ? (
                <th scope="col" className={styles.tick}>
                  <input
                    ref={allBox}
                    type="checkbox"
                    checked={all}
                    disabled={rows.length === 0}
                    aria-label="Select all rows shown"
                    onChange={() => setSelected(all ? new Set() : new Set(keys))}
                  />
                </th>
              ) : null}
              {rowNumbers ? (
                <th scope="col" className={styles.number}>
                  <span aria-hidden="true">#</span>
                  <span className="visuallyHidden">Row number</span>
                </th>
              ) : null}
              {visible.map((column, at) => {
                const direction = sortFor(column);
                const level = sortLevel(column);
                const sortedDirection = sorts.find((s) => s.columnId === column.id)?.direction ?? null;
                const filterId = filterFor(column);
                return (
                  <th
                    key={column.id}
                    ref={(node) => {
                      headRefs.current[column.id] = node;
                    }}
                    scope="col"
                    className={at < pinnedCount ? styles.pinned : undefined}
                    style={{ ...(column.width ? { width: column.width } : {}), ...pinStyle(at) }}
                    data-align={alignOf(column)}
                    aria-sort={direction}
                    onKeyDown={
                      hasMenu
                        ? (event) => {
                            if (event.altKey && event.key === "ArrowDown") {
                              event.preventDefault();
                              setMenuFor(column.id);
                            }
                          }
                        : undefined
                    }
                  >
                    <span className={styles.headInner}>
                      {column.sortable ? (
                        <button
                          type="button"
                          className={styles.sortButton}
                          onClick={(event) => cycle(column, event.shiftKey)}
                        >
                          {column.header}
                          {direction === "ascending" ? (
                            <CaretUp size={12} weight="bold" aria-hidden="true" />
                          ) : direction === "descending" ? (
                            <CaretDown size={12} weight="bold" aria-hidden="true" />
                          ) : (
                            <CaretUpDown size={12} aria-hidden="true" data-sort-neutral="" />
                          )}
                          {level !== null ? (
                            <span className={styles.sortLevel} data-testid="sort-level">
                              {level}
                            </span>
                          ) : null}
                        </button>
                      ) : (
                        <span className={column.headerHidden ? "visuallyHidden" : undefined}>{column.header}</span>
                      )}
                      {columnMenu ? columnMenu(column) : null}
                      {power ? (
                        <ColumnMenu
                          header={column.header}
                          sortable={column.sortable === true}
                          sorted={sortedDirection}
                          canFilter={filterId !== null}
                          canGroup={column.groupable ?? column.value !== undefined}
                          grouped={power.groupBy === column.id}
                          canHide={column !== first && column.hideable !== false}
                          canPin={at !== 0}
                          pinned={power.pinned.includes(column.id)}
                          open={menuFor === column.id}
                          onOpenChange={(open) => setMenuFor(open ? column.id : null)}
                          onSort={(direction) => power.setSorts([{ columnId: column.id, direction }])}
                          onClearSort={() => power.setSorts(power.sorts.filter((s) => s.columnId !== column.id))}
                          onFilter={() => filterId && power.requestFilter(filterId)}
                          onGroup={() => power.setGroupBy(power.groupBy === column.id ? null : column.id)}
                          onHide={() => power.toggleHidden(column.id)}
                          onPin={() => power.togglePin(column.id)}
                        />
                      ) : null}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody ref={tbodyRef}>{body}</tbody>
          {totals && !loading && !error && rows.length > 0 ? (
            <tfoot>
              <tr>
                {selectable ? <td className={styles.tick} /> : null}
                {rowNumbers ? <td className={styles.number} /> : null}
                {visible.map((column, at) => (
                  <td
                    key={column.id}
                    className={cellClass(at)}
                    style={pinStyle(at)}
                    data-align={alignOf(column)}
                    data-numeric={alignOf(column) === "right" ? "true" : undefined}
                  >
                    {totals.values[column.id] ?? (at === 0 ? (totals.label ?? "Total") : null)}
                  </td>
                ))}
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
      {peekRow !== undefined && power && renderPeek ? (
        <PeekPanel
          title={rowLabel?.(peekRow) ?? "Row"}
          onClose={closePeek}
          onOpen={onRowOpen ? () => onRowOpen(peekRow) : undefined}
        >
          {renderPeek(peekRow)}
        </PeekPanel>
      ) : null}
      {power ? <ShortcutSheet open={power.shortcutsOpen} onClose={() => power.setShortcutsOpen(false)} /> : null}
    </div>
  );
}
